import React, { useState, useEffect, useCallback } from 'react';
import { orderService } from '../services/api';
import { GlassPanel, GlassButton, GlassCard, GlassInput, GlassSelect } from '../components/GlassUI';
import StatusBadge from '../components/StatusBadge';
import PhotoUpload from '../components/PhotoUpload';
import PaymentsPanel, { PaymentSummary } from '../components/PaymentsPanel';
import { FaArrowLeft, FaRupeeSign, FaUser, FaCalendar, FaCheck, FaTimes, FaWeight } from 'react-icons/fa';

interface OrderDetailPageProps {
  orderId: string;
  onBack: () => void;
}

const STAGES = [
  { key: 'STAGE_1_PLACEMENT', label: 'Placed', num: 1 },
  { key: 'STAGE_2_KARIGAR', label: 'Karigar', num: 2 },
  { key: 'STAGE_3_QC', label: 'QC', num: 3 },
  { key: 'STAGE_4_LEDGER', label: 'Ledger', num: 4 },
  { key: 'STAGE_5_DELIVERY', label: 'Delivered', num: 5 },
];

const STAGE_INDEX: Record<string, number> = {
  STAGE_1_PLACEMENT: 0,
  STAGE_2_KARIGAR: 1,
  STAGE_3_QC: 2,
  STAGE_4_LEDGER: 3,
  STAGE_5_DELIVERY: 4,
  STAGE_5_DELIVERED: 4,
};

export const OrderDetailPage: React.FC<OrderDetailPageProps> = ({ orderId, onBack }) => {
  const [order, setOrder] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Stage 2 form
  const [karigarName, setKarigarName] = useState('');
  const [karigarDate, setKarigarDate] = useState('');
  const [karigarContact, setKarigarContact] = useState('');

  // Stage 3 form
  const [qcPhotos, setQcPhotos] = useState<string[]>([]);
  const [qcGrossWeight, setQcGrossWeight] = useState('');
  const [qcVA, setQcVA] = useState('');
  const [qcNetWeight, setQcNetWeight] = useState('');
  const [qcStoneCost, setQcStoneCost] = useState('');
  const [qcExecutiveName, setQcExecutiveName] = useState('');
  const [qcMatchOriginal, setQcMatchOriginal] = useState<boolean | null>(null);
  const [qcMismatchReason, setQcMismatchReason] = useState('');
  const [qcNewExpectedDate, setQcNewExpectedDate] = useState('');

  // Stage 4 form
  const [paymentStoneMethod, setPaymentStoneMethod] = useState('CASH');
  const [paymentStoneAmount, setPaymentStoneAmount] = useState('');
  const [paymentMetalAmount, setPaymentMetalAmount] = useState('');
  const [paymentMetalWeight, setPaymentMetalWeight] = useState('');
  const [paymentClearanceDate, setPaymentClearanceDate] = useState('');

  // Manual payment ledger roll-up
  const [manualSummary, setManualSummary] = useState<PaymentSummary | null>(null);

  // Stage 5 form
  const [deliverySlipPhotos, setDeliverySlipPhotos] = useState<string[]>([]);
  const [deliverySalesPerson, setDeliverySalesPerson] = useState('');
  const [deliveryTodayRate, setDeliveryTodayRate] = useState('');
  const [deliveryPurity, setDeliveryPurity] = useState('22K');
  const [deliveryAmountReceived, setDeliveryAmountReceived] = useState('');

  const loadOrder = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await orderService.getById(orderId);
      const o = res.data.order || res.data;
      setOrder(o);
      // Pre-fill Stage 2
      if (o.karigarName) setKarigarName(o.karigarName);
      if (o.karigarDate) setKarigarDate(o.karigarDate.split('T')[0]);
      if (o.karigarContact) setKarigarContact(o.karigarContact);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load order');
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    loadOrder();
  }, [loadOrder]);

  const currentStage = order?.currentStage || 'STAGE_1_PLACEMENT';
  const currentStageIdx = STAGE_INDEX[currentStage] ?? 0;
  const remainingBalance =
    order?.remainingBalance ??
    (order?.totalAmount ?? order?.finalAmount ?? 0) - (order?.totalAdvance ?? order?.cashAdvance ?? order?.advanceAmount ?? 0);
  const finalBalance = remainingBalance - (Number(deliveryAmountReceived) || 0);

  const handleStage2Submit = async () => {
    if (!karigarName.trim()) {
      alert('Worker name is required');
      return;
    }
    setSubmitting(true);
    try {
      await orderService.updateStage(orderId, {
        targetStage: 'STAGE_2_KARIGAR',
        karigarName: karigarName.trim(),
        workerCommitmentDate: karigarDate || undefined,
      });
      alert('Karigar assigned successfully');
      loadOrder();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to update stage');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStage3Submit = async (advance: boolean) => {
    setSubmitting(true);
    try {
      if (advance) {
        const qcBase = {
          receivedGrossWeight: qcGrossWeight ? Number(qcGrossWeight) : undefined,
          receivedVA: qcVA ? Number(qcVA) : undefined,
          receivedNetWeight: qcNetWeight ? Number(qcNetWeight) : undefined,
          finalStoneCost: qcStoneCost ? Number(qcStoneCost) : undefined,
          receivingExecutiveName: qcExecutiveName || undefined,
        };
        if (qcMatchOriginal === false) {
          await orderService.updateStage(orderId, {
            targetStage: 'STAGE_3_QC',
            qcMatchConfirmed: false,
            qcReturnReason: qcMismatchReason.trim(),
            qcNewExpectedDate: qcNewExpectedDate || undefined,
            ...qcBase,
          });
          alert('Order sent back to Stage 2 (Karigar)');
        } else {
          await orderService.updateStage(orderId, {
            targetStage: 'STAGE_4_LEDGER',
            qcMatchConfirmed: true,
            ...qcBase,
          });
          alert('QC approved. Moved to Ledger Clearance.');
        }
      }
      loadOrder();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to update stage');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStage4Submit = async () => {
    if (!paymentClearanceDate) {
      alert('Payment clearance date is required');
      return;
    }
    setSubmitting(true);
    try {
      await orderService.updateStage(orderId, {
        targetStage: 'STAGE_5_DELIVERY',
        stonePaymentMethod: paymentStoneMethod,
        stonePaymentAmount: paymentStoneAmount ? Number(paymentStoneAmount) : 0,
        metalPaymentAmount: paymentMetalAmount ? Number(paymentMetalAmount) : 0,
        metalPaymentWeight: paymentMetalWeight ? Number(paymentMetalWeight) : 0,
        paymentClearanceDate,
      });
      alert('Payment cleared. Moved to Delivery.');
      loadOrder();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to update stage');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStage5Submit = async (complete: boolean) => {
    setSubmitting(true);
    try {
      await orderService.updateStage(orderId, {
        targetStage: 'STAGE_5_DELIVERY',
        actualDeliveryDate: new Date().toISOString(),
        orderSlipPicture: deliverySlipPhotos[0] || undefined,
        dispatchedSalesPersonName: deliverySalesPerson || undefined,
        finalGoldRate: deliveryTodayRate ? Number(deliveryTodayRate) : undefined,
        finalReceivedPurity: deliveryPurity || undefined,
        finalAmountReceived: deliveryAmountReceived ? Number(deliveryAmountReceived) : 0,
      });
      if (complete) {
        alert('Order completed successfully!');
      } else {
        alert('Delivery details saved.');
      }
      loadOrder();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to update');
    } finally {
      setSubmitting(false);
    }
  };

  const handleApproveOrder = async () => {
    setSubmitting(true);
    try {
      await orderService.approve(orderId);
      alert('Order approved');
      loadOrder();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to approve');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-4 border-slate-300 border-t-slate-900 rounded-full mx-auto mb-4" />
          <p className="text-slate-500">Loading order...</p>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="max-w-3xl mx-auto">
        <GlassCard className="p-6 text-center">
          <p className="text-red-600 mb-4">{error || 'Order not found'}</p>
          <GlassButton variant="secondary" onClick={onBack}>Go Back</GlassButton>
        </GlassCard>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Back button */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4 transition-all"
      >
        <FaArrowLeft /> Back to Orders
      </button>

      {/* Order Header */}
      <GlassPanel className="p-6 mb-6">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-1">
              <h1 className="text-2xl md:text-3xl font-bold text-slate-900">{order.invoiceNumber}</h1>
              <StatusBadge
                stage={currentStage}
                type="order"
                karigarDelayed={order.karigarDelayed}
              />
            </div>
            <p className="text-slate-500 flex items-center gap-1">
              <FaUser size={12} /> {order.customer?.customerName || 'N/A'}
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-slate-500">Total Amount</p>
            <p className="text-2xl font-bold text-slate-900 flex items-center gap-1 justify-end">
              <FaRupeeSign className="text-amber-500" size={16} />
              {(order.totalAmount ?? order.finalAmount ?? 0).toFixed(2)}
            </p>
          </div>
        </div>
      </GlassPanel>

      {/* Stage Progress Bar */}
      <GlassCard className="p-4 mb-6">
        <div className="flex items-center justify-between">
          {STAGES.map((stage, idx) => {
            const isCompleted = idx < currentStageIdx;
            const isCurrent = idx === currentStageIdx;
            return (
              <React.Fragment key={stage.key}>
                <div className="flex flex-col items-center">
                  <div
                    className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold transition-all ${
                      isCompleted
                        ? 'bg-green-500 text-white'
                        : isCurrent
                        ? 'bg-slate-900 text-white ring-4 ring-slate-200'
                        : 'bg-slate-200 text-slate-500'
                    }`}
                  >
                    {isCompleted ? <FaCheck size={13} /> : stage.num}
                  </div>
                  <span className={`text-[10px] sm:text-xs mt-1 font-medium text-center leading-tight ${isCurrent ? 'text-slate-900' : 'text-slate-400'}`}>
                    {stage.label}
                  </span>
                </div>
                {idx < STAGES.length - 1 && (
                  <div className={`flex-1 h-1 mx-0.5 sm:mx-1 rounded ${idx < currentStageIdx ? 'bg-green-500' : 'bg-slate-200'}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </GlassCard>

      {/* Stage 1 - Order Placement (Reference) */}
      <GlassPanel className="p-6 mb-6">
        <h2 className="text-lg font-bold text-slate-900 mb-4">Order Details</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
          <div>
            <span className="text-slate-500">Item Name</span>
            <p className="font-semibold text-slate-900">{order.itemName || order.items?.[0]?.itemDescription || order.items?.[0]?.name || 'N/A'}</p>
          </div>
          <div>
            <span className="text-slate-500">Purity</span>
            <p className="font-semibold text-slate-900">{order.items?.[0]?.purity || 'N/A'}</p>
          </div>
          <div>
            <span className="text-slate-500">Net Weight</span>
            <p className="font-semibold text-slate-900">{order.items?.[0]?.weight || 'N/A'}g</p>
          </div>
          <div>
            <span className="text-slate-500">Gold Rate</span>
            <p className="font-semibold text-slate-900">₹{(order.todayGoldRate ?? order.items?.[0]?.rate)?.toLocaleString() || 'N/A'}</p>
          </div>
          <div>
            <span className="text-slate-500">Making Charge</span>
            <p className="font-semibold text-slate-900">₹{Number(order.makingCharges ?? order.items?.[0]?.makingCharge ?? 0).toLocaleString()}</p>
          </div>
          <div>
            <span className="text-slate-500">Stone Cost</span>
            <p className="font-semibold text-slate-900">₹{Number(order.stoneBeadsPolishCost ?? order.items?.[0]?.stones ?? 0).toLocaleString()}</p>
          </div>
        </div>

        <div className="border-t border-slate-200 mt-4 pt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-slate-500">Total Amount</span>
            <p className="font-bold text-slate-900">₹{(order.totalAmount ?? order.finalAmount ?? 0).toFixed(2)}</p>
          </div>
          <div>
            <span className="text-slate-500">
              Advance Paid{manualSummary && (manualSummary.paidCount + manualSummary.unpaidCount) > 0 ? ' (manual)' : ''}
            </span>
            <p className="font-semibold text-green-600">
              ₹{(manualSummary && (manualSummary.paidCount + manualSummary.unpaidCount) > 0
                ? manualSummary.totalPaid
                : (order.totalAdvance ?? order.cashAdvance ?? order.advanceAmount ?? 0)).toFixed(2)}
            </p>
          </div>
          <div>
            <span className="text-slate-500">
              Remaining Balance{manualSummary && (manualSummary.paidCount + manualSummary.unpaidCount) > 0 ? ' (manual)' : ''}
            </span>
            <p className="font-semibold text-red-600">
              ₹{(manualSummary && (manualSummary.paidCount + manualSummary.unpaidCount) > 0
                ? (manualSummary.totalUnpaid || manualSummary.balanceAfter)
                : remainingBalance).toFixed(2)}
            </p>
          </div>
          <div>
            <span className="text-slate-500">Order Date</span>
            <p className="font-semibold text-slate-900">{new Date(order.orderDate).toLocaleDateString()}</p>
          </div>
        </div>

        {/* Management Approval */}
        {!order.managementApproved && order.managementApproved !== undefined && (
          <div className="mt-4 pt-4 border-t border-slate-200">
            <GlassCard className="bg-yellow-50 border-yellow-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-yellow-800">Pending Management Approval</p>
                  <p className="text-sm text-yellow-600">This order requires approval before processing</p>
                </div>
                <GlassButton
                  variant="primary"
                  className="text-sm shrink-0"
                  onClick={handleApproveOrder}
                  disabled={submitting}
                >
                  Approve
                </GlassButton>
              </div>
            </GlassCard>
          </div>
        )}
      </GlassPanel>

      {/* Manual payments / advances — records only */}
      <div className="mb-6">
        <PaymentsPanel
          kind="order"
          recordId={orderId}
          totalAmount={order.totalAmount ?? order.finalAmount ?? 0}
          onChange={(s: PaymentSummary) => setManualSummary(s)}
        />
      </div>

      {/* Stage 2 - Karigar Assignment */}
      {currentStageIdx >= 1 && (
        <GlassPanel className="p-6 mb-6">
          <h2 className="text-lg font-bold text-slate-900 mb-4">Stage 2: Karigar Assignment</h2>
          {currentStage === 'STAGE_2_KARIGAR' ? (
            <div className="space-y-4">
              <div>
                <label className="block text-slate-700 font-semibold mb-1 text-sm">Worker / Karigar Name *</label>
                <GlassInput
                  value={karigarName}
                  onChange={e => setKarigarName(e.target.value)}
                  placeholder="Enter karigar name"
                />
              </div>
              <div>
                <label className="block text-slate-700 font-semibold mb-1 text-sm">
                  <FaCalendar className="inline mr-1" /> Commitment Date
                </label>
                <GlassInput
                  type="date"
                  value={karigarDate}
                  onChange={e => setKarigarDate(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-slate-700 font-semibold mb-1 text-sm">
                  <FaUser className="inline mr-1" /> Karigar Contact (Optional)
                </label>
                <GlassInput
                  value={karigarContact}
                  onChange={e => setKarigarContact(e.target.value)}
                  placeholder="Phone number"
                />
              </div>
              <GlassButton
                variant="primary"
                onClick={handleStage2Submit}
                disabled={submitting}
              >
                {submitting ? 'Assigning...' : 'Assign & Proceed to QC'}
              </GlassButton>
            </div>
          ) : (
            <div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-500">Worker Name</span>
                  <p className="font-semibold text-slate-900">{order.karigarName || 'N/A'}</p>
                </div>
                <div>
                  <span className="text-slate-500">Commitment Date</span>
                  <p className="font-semibold text-slate-900">
                    {order.workerCommitmentDate
                      ? new Date(order.workerCommitmentDate).toLocaleDateString()
                      : order.karigarDate
                      ? new Date(order.karigarDate).toLocaleDateString()
                      : 'N/A'}
                  </p>
                </div>
              </div>
              {currentStage === 'STAGE_2_KARIGAR' && (
                <GlassButton
                  variant="primary"
                  className="mt-4 w-full sm:w-auto"
                  onClick={async () => {
                    setSubmitting(true);
                    try {
                      await orderService.updateStage(orderId, { targetStage: 'STAGE_3_QC' });
                      alert('Moved to QC stage');
                      loadOrder();
                    } catch (err: any) {
                      alert(err.response?.data?.error || 'Failed to move to QC');
                    } finally {
                      setSubmitting(false);
                    }
                  }}
                  disabled={submitting}
                >
                  Ready for QC →
                </GlassButton>
              )}
            </div>
          )}
        </GlassPanel>
      )}

      {/* Stage 3 - QC & Receiving */}
      {currentStageIdx >= 2 && currentStage === 'STAGE_3_QC' && (
        <GlassPanel className="p-6 mb-6">
          <h2 className="text-lg font-bold text-slate-900 mb-4">Stage 3: QC & Receiving</h2>
          <div className="space-y-4">
            <PhotoUpload
              photos={qcPhotos}
              onPhotosChange={setQcPhotos}
              capture="environment"
              label="Item Received Photo"
            />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-slate-700 font-semibold mb-1 text-sm">
                  <FaWeight className="inline mr-1" /> Received Gross Weight (g)
                </label>
                <GlassInput
                  type="number"
                  step="0.01"
                  value={qcGrossWeight}
                  onChange={e => setQcGrossWeight(e.target.value)}
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="block text-slate-700 font-semibold mb-1 text-sm">Received VA (%)</label>
                <GlassInput
                  type="number"
                  step="0.01"
                  value={qcVA}
                  onChange={e => setQcVA(e.target.value)}
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="block text-slate-700 font-semibold mb-1 text-sm">Received Net Weight (g)</label>
                <GlassInput
                  type="number"
                  step="0.01"
                  value={qcNetWeight}
                  onChange={e => setQcNetWeight(e.target.value)}
                  placeholder="0.00"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-slate-700 font-semibold mb-1 text-sm">Final Stone Cost (₹)</label>
                <GlassInput
                  type="number"
                  step="0.01"
                  value={qcStoneCost}
                  onChange={e => setQcStoneCost(e.target.value)}
                  placeholder="0"
                />
              </div>
              <div>
                <label className="block text-slate-700 font-semibold mb-1 text-sm">
                  <FaUser className="inline mr-1" /> Receiving Executive Name
                </label>
                <GlassInput
                  value={qcExecutiveName}
                  onChange={e => setQcExecutiveName(e.target.value)}
                  placeholder="Name"
                />
              </div>
            </div>

            <div className="border-t border-slate-200 pt-4">
              <label className="block text-slate-700 font-semibold mb-2 text-sm">Does item match original specs from Sales Person?</label>
              <div className="flex gap-3 mb-4">
                <GlassButton
                  variant={qcMatchOriginal === true ? 'primary' : 'secondary'}
                  onClick={() => setQcMatchOriginal(true)}
                  className="flex-1"
                >
                  <FaCheck className="inline mr-1" /> Yes - Match
                </GlassButton>
                <GlassButton
                  variant={qcMatchOriginal === false ? 'primary' : 'secondary'}
                  onClick={() => setQcMatchOriginal(false)}
                  className="flex-1"
                >
                  <FaTimes className="inline mr-1" /> No - Mismatch
                </GlassButton>
              </div>

              {qcMatchOriginal === false && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-slate-700 font-semibold mb-1 text-sm">Mismatch Reason</label>
                    <GlassInput
                      value={qcMismatchReason}
                      onChange={e => setQcMismatchReason(e.target.value)}
                      placeholder="Describe the mismatch"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-700 font-semibold mb-1 text-sm">New Expected Date</label>
                    <GlassInput
                      type="date"
                      value={qcNewExpectedDate}
                      onChange={e => setQcNewExpectedDate(e.target.value)}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-3">
              {qcMatchOriginal === false ? (
                <GlassButton
                  variant="primary"
                  className="flex-1 bg-orange-600 hover:bg-orange-700"
                  onClick={() => handleStage3Submit(true)}
                  disabled={submitting || !qcMismatchReason.trim()}
                >
                  {submitting ? 'Saving...' : 'Send Back to Karigar'}
                </GlassButton>
              ) : (
                <GlassButton
                  variant="primary"
                  className="flex-1"
                  onClick={() => handleStage3Submit(true)}
                  disabled={submitting || qcMatchOriginal === null}
                >
                  {submitting ? 'Saving...' : 'Approve QC → Ledger'}
                </GlassButton>
              )}
            </div>
          </div>
        </GlassPanel>
      )}

      {/* Stage 4 - Ledger Clearance */}
      {currentStageIdx >= 3 && currentStage === 'STAGE_4_LEDGER' && (
        <GlassPanel className="p-6 mb-6">
          <h2 className="text-lg font-bold text-slate-900 mb-4">Stage 4: Ledger Clearance</h2>
          <div className="space-y-4">
            <div className="border-b border-slate-200 pb-4">
              <h3 className="font-semibold text-slate-700 mb-2 text-sm">Payment for Stones</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <GlassSelect
                  value={paymentStoneMethod}
                  onChange={e => setPaymentStoneMethod(e.target.value)}
                >
                  <option value="CASH">Cash</option>
                  <option value="ONLINE">Online</option>
                  <option value="UPI">UPI</option>
                </GlassSelect>
                <GlassInput
                  type="number"
                  step="0.01"
                  value={paymentStoneAmount}
                  onChange={e => setPaymentStoneAmount(e.target.value)}
                  placeholder="Stone payment amount (₹)"
                />
              </div>
            </div>

            <div className="border-b border-slate-200 pb-4">
              <h3 className="font-semibold text-slate-700 mb-2 text-sm">Metal Payment</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <GlassInput
                  type="number"
                  step="0.01"
                  value={paymentMetalAmount}
                  onChange={e => setPaymentMetalAmount(e.target.value)}
                  placeholder="Metal amount (₹)"
                />
                <GlassInput
                  type="number"
                  step="0.01"
                  value={paymentMetalWeight}
                  onChange={e => setPaymentMetalWeight(e.target.value)}
                  placeholder="Metal weight (g)"
                />
              </div>
            </div>

            <div>
              <label className="block text-slate-700 font-semibold mb-1 text-sm">
                <FaCalendar className="inline mr-1" /> Payment Clearance Date *
              </label>
              <GlassInput
                type="date"
                value={paymentClearanceDate}
                onChange={e => setPaymentClearanceDate(e.target.value)}
              />
            </div>

            <GlassCard className="bg-slate-50 border-slate-200">
              <p className="text-sm text-slate-600">Remaining Balance to Clear</p>
              <p className="text-xl font-bold text-red-600 flex items-center gap-1">
                <FaRupeeSign size={14} />{remainingBalance.toFixed(2)}
              </p>
            </GlassCard>

            <GlassButton
              variant="primary"
              className="w-full"
              onClick={handleStage4Submit}
              disabled={submitting}
            >
              {submitting ? 'Processing...' : 'Clear Payment → Move to Delivery'}
            </GlassButton>
          </div>
        </GlassPanel>
      )}

      {/* Stage 5 - Final Delivery */}
      {currentStageIdx >= 4 && (currentStage === 'STAGE_5_DELIVERY' || currentStage === 'STAGE_5_DELIVERED') && (
        <GlassPanel className="p-6 mb-6">
          <h2 className="text-lg font-bold text-slate-900 mb-4">Stage 5: Final Delivery</h2>
          <div className="space-y-4">
            <PhotoUpload
              photos={deliverySlipPhotos}
              onPhotosChange={setDeliverySlipPhotos}
              capture="environment"
              label="Order Slip Picture"
            />
            <div>
              <label className="block text-slate-700 font-semibold mb-1 text-sm">
                <FaUser className="inline mr-1" /> Dispatched Sales Person Name
              </label>
              <GlassInput
                value={deliverySalesPerson}
                onChange={e => setDeliverySalesPerson(e.target.value)}
                placeholder="Name"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-slate-700 font-semibold mb-1 text-sm">Today's Gold Rate (₹/g)</label>
                <GlassInput
                  type="number"
                  step="0.01"
                  value={deliveryTodayRate}
                  onChange={e => setDeliveryTodayRate(e.target.value)}
                  placeholder="Rate"
                />
              </div>
              <div>
                <label className="block text-slate-700 font-semibold mb-1 text-sm">Received Purity</label>
                <GlassSelect value={deliveryPurity} onChange={e => setDeliveryPurity(e.target.value)}>
                  <option value="9K">9K</option>
                  <option value="14K">14K</option>
                  <option value="18K">18K</option>
                  <option value="22K">22K</option>
                  <option value="24K">24K</option>
                </GlassSelect>
              </div>
              <div>
                <label className="block text-slate-700 font-semibold mb-1 text-sm">Total Amount Received (₹)</label>
                <GlassInput
                  type="number"
                  step="0.01"
                  value={deliveryAmountReceived}
                  onChange={e => setDeliveryAmountReceived(e.target.value)}
                  placeholder="0.00"
                />
              </div>
            </div>

            <GlassCard className={`border ${finalBalance <= 0 ? 'bg-green-50 border-green-200' : 'bg-orange-50 border-orange-200'}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Final Balance</p>
                  <p className={`text-2xl font-bold ${finalBalance <= 0 ? 'text-green-600' : 'text-orange-600'} flex items-center gap-1`}>
                    <FaRupeeSign size={16} />{finalBalance.toFixed(2)}
                  </p>
                </div>
                {finalBalance <= 0 && (
                  <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-semibold">
                    Fully Paid
                  </span>
                )}
              </div>
            </GlassCard>

            <div className="flex gap-3">
              <GlassButton
                variant="secondary"
                className="flex-1"
                onClick={() => handleStage5Submit(false)}
                disabled={submitting}
              >
                {submitting ? 'Saving...' : 'Save Delivery Details'}
              </GlassButton>
              {finalBalance <= 0 && (
                <GlassButton
                  variant="primary"
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  onClick={() => handleStage5Submit(true)}
                  disabled={submitting}
                >
                  {submitting ? 'Completing...' : 'Complete Order'}
                </GlassButton>
              )}
            </div>
          </div>
        </GlassPanel>
      )}

      {/* Karigar Delay Warning */}
      {order.karigarDelayed && (
        <GlassCard className="p-4 bg-red-50 border-red-200 mb-6">
          <p className="text-red-700 font-semibold">Order is delayed beyond karigar commitment date</p>
          {order.delayedReason && (
            <p className="text-red-600 text-sm mt-1">Reason: {order.delayedReason}</p>
          )}
        </GlassCard>
      )}
    </div>
  );
};

export default OrderDetailPage;
